import type * as Monaco from "monaco-editor/esm/vs/editor/editor.api";
import type { editor } from "monaco-editor";
import {
  getJsEditorRuntimeCompletions,
  getMemberExpressionInfo,
} from "./jsEditorRuntimeContext";
import type {
  JsEditorCompletionKind,
  JsEditorRuntimeCompletionContext,
} from "./jsEditorRuntimeContext";

type MonacoApi = typeof Monaco;

const modelContexts = new Map<string, JsEditorRuntimeCompletionContext>();
let completionProviderDisposable: Monaco.IDisposable | undefined;

export const ensureJsEditorRuntimeContext = (monaco: MonacoApi) => {
  const {
    javascriptDefaults,
    ScriptTarget,
    ModuleKind,
  } = monaco.languages.typescript;

  javascriptDefaults.setCompilerOptions({
    allowNonTsExtensions: true,
    allowJs: true,
    // Prevent untyped member access from falling back to scope-name suggestions.
    checkJs: true,
    target: ScriptTarget.ESNext,
    module: ModuleKind.ESNext,
  });
  javascriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: true,
    noSuggestionDiagnostics: true,
  });

  if (completionProviderDisposable) return;

  completionProviderDisposable = monaco.languages.registerCompletionItemProvider(
    "javascript",
    {
      triggerCharacters: ["."],
      provideCompletionItems(model, position) {
        const context = modelContexts.get(model.uri.toString());
        if (!context) return { suggestions: [] };

        const lineUntilPosition = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });
        const memberExpression = getMemberExpressionInfo(lineUntilPosition);
        if (!memberExpression) return { suggestions: [] };

        const completions = getJsEditorRuntimeCompletions(
          context,
          lineUntilPosition
        );
        const word = model.getWordUntilPosition(position);

        return {
          suggestions: completions.map((completion) => {
            const replacePreviousDot =
              completion.replacePreviousDot && memberExpression.hasMemberAccess;
            const startColumn = replacePreviousDot
              ? Math.max(1, position.column - word.word.length - 1)
              : word.startColumn;

            return {
              label: completion.label,
              kind: getMonacoCompletionKind(monaco, completion.kind),
              insertText: completion.insertText,
              insertTextRules: completion.insertText.includes("${")
                ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet
                : undefined,
              detail: completion.detail,
              documentation: completion.documentation,
              range: {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn,
                endColumn: word.endColumn,
              },
            };
          }),
        };
      },
    }
  );
};

export const setJsEditorModelRuntimeContext = (
  model: editor.ITextModel | null,
  context: JsEditorRuntimeCompletionContext
) => {
  if (!model) return;
  modelContexts.set(model.uri.toString(), context);
};

export const clearJsEditorModelRuntimeContext = (
  model: editor.ITextModel | null
) => {
  if (!model) return;
  modelContexts.delete(model.uri.toString());
};

const getMonacoCompletionKind = (
  monaco: MonacoApi,
  kind: JsEditorCompletionKind
) => {
  switch (kind) {
    case "method":
      return monaco.languages.CompletionItemKind.Method;
    case "enum":
      return monaco.languages.CompletionItemKind.EnumMember;
    case "variable":
      return monaco.languages.CompletionItemKind.Variable;
    case "property":
    default:
      return monaco.languages.CompletionItemKind.Property;
  }
};
